import { observable, action, computed } from 'mobx'
import {
  CreateInvoice,
  IInvoice,
  IBatchInInvoiceLine,
  InvoiceStatus,
} from '@getgreenline/homi-shared'
import { InventoryLogAPI } from '@getgreenline/inventory'
import { IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import { GrPrice } from '../../../../utilities/helpers'
import moment from 'moment'
import { isNil } from 'lodash'

export class CreateInvoiceDetail extends CreateInvoice {
  @observable
  invoiceDetail?: IInvoice

  @observable
  isEditMode = false

  @observable
  isSaved = false

  @observable
  inventoryLogs: IInventoryLog[] = []

  @computed
  get isCompletedInCurrentMonth() {
    if (isNil(this.completeDate)) return false
    else {
      const presentDate = moment.utc()
      const isCompletedCurrentMonth = moment(this.completeDate).isSame(presentDate, 'month')
      return isCompletedCurrentMonth
    }
  }

  constructor() {
    super()
  }

  getInventoryLogs = async (
    companyId: number,
    limit: number,
    offset: number,
  ): Promise<IInventoryLog[]> => {
    if (!this.invoiceDetail) {
      return []
    }

    const { data } = await InventoryLogAPI.getInventoryLogs(companyId, {
      limit,
      offset,
      invoiceId: this.invoiceDetail.id,
    })
    return data
  }

  @action
  setIsSaved = (isSaved: boolean) => {
    this.isSaved = isSaved
  }

  @action
  setInventoryLogs = (inventoryLogs: IInventoryLog[]) => {
    this.inventoryLogs = inventoryLogs

    this.setIsEdited(true)
  }

  @action
  setInvoiceDetail = (invoiceDetail: IInvoice) => {
    this.invoiceDetail = invoiceDetail
  }

  @action
  setIsEditMode = (isEditMode: boolean) => {
    this.isEditMode = isEditMode
  }

  createLineBatchInfoForCsv = (batches: IBatchInInvoiceLine[]): string => {
    // Make inline batch string. e.g.) "batch name1": "batch quantity1", "batch name2": "batch quantity2"
    const batchLineArray = batches.map((batch) => `${batch.batchNumber}: ${batch.quantity}`)
    return batchLineArray.join(', ')
  }

  get isNewInvoiceDetail(): boolean {
    return this.invoiceDetail === undefined
  }

  get isEditable(): boolean {
    if (!this.invoiceDetail) {
      return true
    }

    return (
      this.invoiceDetail.status === InvoiceStatus.PENDING ||
      this.invoiceDetail.status === InvoiceStatus.IN_PROGRESS
    )
  }

  get invoiceLinesInCsv() {
    return this.lines.map((line) => {
      return {
        SKU: line.sku,
        Product: line.productName,
        Unit: line.productUnit,
        'Quantity ordered': line.quantity,
        'Quantity received': line.quantityReceived,
        'Cost per unit': line.quantity
          ? GrPrice.convertCentToDollar(line.total / line.quantity)
          : 0,
        Total: GrPrice.convertCentToDollar(line.total),
        Notes: line.notes,
        'Lot #: Qty': this.createLineBatchInfoForCsv(line.batches),
      }
    })
  }

  get renderedStatus() {
    if (!this.invoiceDetail) {
      return
    }

    const statusObject = { name: '', color: '' }

    switch (true) {
      case this.invoiceDetail.status === InvoiceStatus.PENDING:
        statusObject.name = 'Pending'
        statusObject.color = 'silver'
        break
      case this.invoiceDetail.status === InvoiceStatus.IN_PROGRESS:
        statusObject.name = 'In progress'
        statusObject.color = 'orange'
        break
      case this.invoiceDetail.status === InvoiceStatus.COMPLETED:
        statusObject.name = 'Completed'
        statusObject.color = 'green'
        break
      default:
        statusObject.name = 'Cancelled'
        statusObject.color = 'red'
    }

    return statusObject
  }

  get linesWithoutMatchingBatchSum() {
    // if quantityReceived is provided and the product is batch tracked, the user must fill in batches
    return this.lines.filter((line) => {
      const { isBatchTracked, quantityReceived, batches } = line
      const batchSum = batches.reduce((prev, next) => prev + next.quantity, 0)

      // If a line is batch-tracked and its quantityReceived exists, check if the batch sum === quantityRec
      if (isBatchTracked && quantityReceived) {
        return !(quantityReceived === batchSum)
      }

      return false
    })
  }

  get batchLinesExist(): boolean {
    return this.lines.some((line) => line.isBatchTracked)
  }
}
